/**
 * AdminUsers Component
 * User management with pagination, search, and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AdminUser, UserPagination } from '../../services/adminService';
import { usersApi } from '../../services/adminService';
import useAdminAuth from '../../hooks/useAdminAuth';
import AdminLayout from './AdminLayout';
import Modal from '../ui/Modal';

type LimitOption = 25 | 50 | 100 | 200;

const AdminUsers: React.FC = () => {
  const { isAdmin, isLoading: authLoading } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState<UserPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filter, setFilter] = useState(searchParams.get('filter') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [limit, setLimit] = useState<LimitOption>(25);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [pendingAdminStatus, setPendingAdminStatus] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  
  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoading(true);
      const data = await usersApi.getUsers(page, limit, search, filter);
      setUsers(data.users);
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, limit, search, filter]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchUsers();
    }
  }, [authLoading, isAdmin, fetchUsers]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filter) params.set('filter', filter);
    if (page > 1) params.set('page', page.toString());
    setSearchParams(params);
  }, [search, filter, page, setSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleEditEmail = async () => {
    if (!selectedUser || !editEmail) return;
    
    setActionLoading(true);
    setActionError(null);
    
    try {
      await usersApi.updateEmail(selectedUser.id, editEmail);
      setShowEditModal(false);
      setSelectedUser(null);
      setEditEmail('');
      fetchUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendVerification = async (user: AdminUser) => {
    setOpenMenuId(null);
    
    try {
      await usersApi.resendVerification(user.id);
      alert(`Verification email sent to ${user.email}`);
    } catch {
      alert('Failed to resend verification email');
    }
  };

  const handleUnlockUser = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    setActionError(null);
    
    try {
      await usersApi.unlockUser(selectedUser.id);
      await fetchUsers(); // Wait for refresh before closing modal
      setShowUnlockModal(false);
      setSelectedUser(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to unlock account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || deleteConfirmation !== 'delete') return;
    
    setActionLoading(true);
    setActionError(null);
    
    try {
      await usersApi.deleteUser(selectedUser.id);
      setShowDeleteModal(false);
      setSelectedUser(null);
      setDeleteConfirmation('');
      fetchUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = (user: AdminUser) => {
    setSelectedUser(user);
    setEditEmail(user.email);
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const openDeleteModal = (user: AdminUser) => {
    setSelectedUser(user);
    setDeleteConfirmation('');
    setShowDeleteModal(true);
    setOpenMenuId(null);
  };

  const openUnlockModal = (user: AdminUser) => {
    setSelectedUser(user);
    setShowUnlockModal(true);
    setOpenMenuId(null);
  };

  const openAdminModal = (user: AdminUser, makeAdmin: boolean) => {
    setSelectedUser(user);
    setPendingAdminStatus(makeAdmin);
    setShowAdminModal(true);
    setOpenMenuId(null);
  };

  const handleSetAdminStatus = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    setActionError(null);

    try {
      await usersApi.setAdminStatus(selectedUser.id, pendingAdminStatus);
      await fetchUsers();
      setShowAdminModal(false);
      setSelectedUser(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update admin status');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage user accounts and verification status</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Users</option>
              <option value="unverified">Unverified Only</option>
              <option value="locked">Locked Only</option>
            </select>
            <select
              value={limit}
              onChange={(e) => { setLimit(parseInt(e.target.value) as LimitOption); setPage(1); }}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
              <option value="200">200 per page</option>
            </select>
            <button
              type="submit"
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Search
            </button>
          </form>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Join Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(user.joinDate)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {user.emailVerified ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Unverified
                            </span>
                          )}
                          {user.isAdmin && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Admin
                            </span>
                          )}
                          {user.isLocked && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              🔒 Locked
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          
                          {openMenuId === user.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                              <button
                                onClick={() => openEditModal(user)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit Email
                              </button>
                              {!user.emailVerified && (
                                <button
                                  onClick={() => handleResendVerification(user)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  Resend Verification
                                </button>
                              )}
                              {user.isLocked && (
                                <button
                                  onClick={() => openUnlockModal(user)}
                                  className="w-full px-4 py-2 text-left text-sm text-teal-700 hover:bg-teal-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                  </svg>
                                  Unlock Account
                                </button>
                              )}
                              {user.isAdmin ? (
                                <button
                                  onClick={() => openAdminModal(user, false)}
                                  className="w-full px-4 py-2 text-left text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                  </svg>
                                  Remove Admin
                                </button>
                              ) : (
                                <button
                                  onClick={() => openAdminModal(user, true)}
                                  className="w-full px-4 py-2 text-left text-sm text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                  </svg>
                                  Make Admin
                                </button>
                              )}
                              <button
                                onClick={() => openDeleteModal(user)}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete Account
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.currentPage * pagination.limit, pagination.totalUsers)} of{' '}
                {pagination.totalUsers} users
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={!pagination.hasPrev}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={!pagination.hasNext}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Email Modal */}
      <Modal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setActionError(null); }}
        title="Edit Email Address"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Update email for <strong>{selectedUser?.name}</strong>
          </p>
          <input
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="New email address"
          />
          {actionError && (
            <p className="text-red-600 text-sm">{actionError}</p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setShowEditModal(false); setActionError(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleEditEmail}
              disabled={actionLoading || !editEmail}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {actionLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Unlock Confirmation Modal */}
      <Modal
        open={showUnlockModal}
        onClose={() => { setShowUnlockModal(false); setActionError(null); }}
        title="Unlock User Account"
      >
        <div className="space-y-4">
          <div className="bg-teal-50 border border-teal-100 rounded-lg p-4">
            <p className="text-teal-800 text-sm">
              This will reset the user's failed login attempts and allow them to sign in immediately.
            </p>
          </div>
          <p className="text-gray-600">
            Are you sure you want to restore access for <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
          </p>
          {actionError && (
            <p className="text-red-600 text-sm">{actionError}</p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setShowUnlockModal(false); setActionError(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUnlockUser}
              disabled={actionLoading}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {actionLoading ? 'Unlocking...' : 'Unlock Account'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setActionError(null); }}
        title="Delete User Account"
      >
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              <strong>Warning:</strong> This action cannot be undone. The user's account and all associated data will be permanently deleted.
            </p>
          </div>
          <p className="text-gray-600">
            To confirm deletion of <strong>{selectedUser?.email}</strong>, type "delete" below:
          </p>
          <input
            type="text"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder='Type "delete" to confirm'
          />
          {actionError && (
            <p className="text-red-600 text-sm">{actionError}</p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setShowDeleteModal(false); setActionError(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteUser}
              disabled={actionLoading || deleteConfirmation !== 'delete'}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {actionLoading ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Admin Status Confirmation Modal */}
      <Modal
        open={showAdminModal}
        onClose={() => { setShowAdminModal(false); setActionError(null); }}
        title={pendingAdminStatus ? 'Grant Admin Privileges' : 'Remove Admin Privileges'}
      >
        <div className="space-y-4">
          {pendingAdminStatus ? (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-purple-800 text-sm">
                <strong>Warning:</strong> This user will have full access to the admin portal, including user management, analytics, and all admin features.
              </p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm">
                This user will lose all admin portal access immediately.
              </p>
            </div>
          )}
          <p className="text-gray-600">
            {pendingAdminStatus
              ? <>Are you sure you want to grant admin privileges to <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?</>
              : <>Are you sure you want to remove admin privileges from <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?</>
            }
          </p>
          {actionError && (
            <p className="text-red-600 text-sm">{actionError}</p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setShowAdminModal(false); setActionError(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSetAdminStatus}
              disabled={actionLoading}
              className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                pendingAdminStatus
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {actionLoading
                ? 'Saving...'
                : pendingAdminStatus ? 'Grant Admin' : 'Remove Admin'
              }
            </button>
          </div>
        </div>
      </Modal>

      {/* Click outside to close menu */}
      {openMenuId && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setOpenMenuId(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminUsers;